import {
  IonContent,
  IonIcon,
  IonPage,
  IonRouterLink,
  IonSpinner,
} from "@ionic/react";
import { logoApple } from "ionicons/icons";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { isNativeiOS } from "../../lib/utils/platform";
import { isEmbroideryTrace } from "../../config/appVariant";
import { useAuth } from "../../contexts/AuthContext";
import {
  handleSignIn,
  signInWithApple,
  signInWithGoogle,
} from "../../lib/firebase/firebase";

import googleicon from "../../assets/google.svg";
import logo from "../../assets/logo1.svg";
import branding from "../../config/branding";

import { faCrown } from "@fortawesome/pro-solid-svg-icons";
import { faEnvelope, faLockKeyhole, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import "./SignIn.css";

const SignIn = () => {
  const history = useHistory();
  const { user, userIsPremium, isLoading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const getReadableError = (errorCode) => {
    switch (errorCode) {
      case "auth/invalid-email":
        return "The email you have entered is not valid. Please check your email and try again.";
      case "auth/user-disabled":
        return "Your account has been disabled. Please contact support for help.";
      case "auth/user-not-found":
        return "No account found with this email. Please check your email or create a new account.";
      case "auth/wrong-password":
        return "Incorrect password entered. Please try again.";
      default:
        return errorCode;
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      onSubmit();
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError("");
      const result = await signInWithGoogle();
      
      if (!result) {
        return;
      }
      
      const user = result.user;
      if (result.additionalUserInfo?.isNewUser) {
        console.log("New user signed up with Google:", user);
      } else {
        console.log("Existing user signed in with Google:", user);
      }
    } catch (error) {
      console.error("Error during Google sign-in:", error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError("Sign-in was cancelled.");
      } else if (error.code === 'auth/cancelled-popup-request') {
        return;
      } else {
        setError("Failed to sign in with Google. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    try {
      setLoading(true);
      setError("");
      const result = await signInWithApple();
      
      if (!result) {
        return;
      }
      
      const user = result.user;
      if (result.additionalUserInfo?.isNewUser) {
        console.log("New user signed up with Apple:", user);
      } else {
        console.log("Existing user signed in with Apple:", user);
      }
    } catch (error) {
      console.error("Error during Apple sign-in:", error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError("Sign-in was cancelled.");
      } else if (error.code === 'auth/cancelled-popup-request') {
        return;
      } else {
        setError("Failed to sign in with Apple. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }
    try {
      setLoading(true);
      await handleSignIn(email, password);
    } catch (error) {
      console.error("Sign-in error:", error);
      setError(getReadableError(error.code));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    
    const oauthRedirectComplete = sessionStorage.getItem('oauthRedirectComplete');
    if (oauthRedirectComplete) {
      sessionStorage.removeItem('oauthRedirectComplete');
      console.log("🔐 SignIn: OAuth redirect detected, redirecting to /home immediately");
      history.push("/home");
      return;
    }
    
    if (authLoading) return;

    const params = new URLSearchParams(window.location.search);
    const redirectTo = params.get("redirect");
    if (redirectTo && redirectTo.startsWith("/")) {
      history.push(redirectTo);
      return;
    }
    
    if (userIsPremium || isNativeiOS() || isEmbroideryTrace) {
      history.push("/home");
    } else {
      history.push("/checkout");
    }
  }, [user, userIsPremium, authLoading, history]);

  useEffect(() => {
    if (!user || !authLoading) return;
    
    const timeout = setTimeout(() => {
      console.log("🔐 SignIn: Timeout - redirecting to /home after 5s of loading");
      history.push("/home");
    }, 5000);
    
    return () => clearTimeout(timeout);
  }, [user, authLoading, history]);

  return (
    <IonPage>
      <IonContent className="auth-background">
        <div className="auth-wrapper">
          <div className="auth-card">
            <div className="auth-logo">
              {isEmbroideryTrace ? (
                <img src={branding.logo} alt={branding.appName} style={{ height: "44px", width: "auto", objectFit: "contain" }} />
              ) : (
                <>
                  <img src={logo} alt="SvgTrace" />
                  <span className="pro-badge">
                    <FontAwesomeIcon icon={faCrown} /> PRO
                  </span>
                </>
              )}
            </div>

            <div className="auth-heading">
              <h2>Welcome back</h2>
              <p>Sign in to your account to continue</p>
            </div>

            {error && (
              <div className="auth-error">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                {error}
              </div>
            )}

            <form className="auth-form" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
              <div className="auth-input-group">
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
              </div>

              <div className="auth-input-group">
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  required
                />
                <FontAwesomeIcon icon={faLockKeyhole} className="input-icon" />
              </div>

              <div className="auth-forgot-link">
                <IonRouterLink routerLink="/forgot-password">
                  Forgot password?
                </IonRouterLink>
              </div>

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading}
              >
                {loading ? <IonSpinner name="crescent" /> : "Sign In"}
              </button>

              <div className="auth-divider">
                <span>or</span>
              </div>

              <div className="auth-social-buttons">
                <button
                  type="button"
                  className="auth-social-btn"
                  onClick={handleGoogleSignIn}
                >
                  <img src={googleicon} alt="Google" />
                  Google
                </button>
                <button
                  type="button"
                  className="auth-social-btn"
                  onClick={handleAppleSignIn}
                >
                  <IonIcon icon={logoApple} />
                  Apple
                </button>
              </div>
            </form>

            <p className="auth-prompt">
              Don't have an account?{" "}
              <IonRouterLink routerLink="/signup">Sign Up</IonRouterLink>
            </p>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SignIn;
